import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { PublicShell } from "@/components/site-chrome";
import { Stamp } from "@/components/stamp";
import { marketingHead, SITE_URL } from "@/lib/seo";

const PATH = "/glossary";
const TITLE = "AI search glossary, GEO, LLMO, AEO, citation rate";
const DESCRIPTION =
  "Plain-English definitions for the terms appearing in AI search strategy: GEO, LLMO, AEO, citation rate, mention quality, buyer journey matrix, and more.";

type Term = {
  term: string;
  short: string;
  long: string;
  alsoSee?: { label: string; to: string }[];
};

const TERMS: Term[] = [
  {
    term: "AEO",
    short: "Answer Engine Optimization.",
    long: "Practice of making your content show up as the cited source when an AI answer engine (ChatGPT, Perplexity, Claude, Gemini) answers a buyer's question. Distinct from SEO because the optimization target is the assistant's response, not a search results page.",
    alsoSee: [{ label: "GEO", to: "/glossary#geo" }, { label: "LLMO vs GEO", to: "/llmo-vs-geo" }, { label: "How it works", to: "/how-it-works" }],
  },
  {
    term: "AI visibility",
    short: "Whether AI assistants surface your brand when buyers ask category-relevant questions.",
    long: "The umbrella metric for AEO/GEO/LLMO programs. Usually tracked as citation rate plus mention quality across a defined prompt set and competitor set.",
    alsoSee: [{ label: "Tracking AI visibility", to: "/ai-visibility-tracking" }, { label: "AI visibility vs LLMO", to: "/llmo-vs-geo" }],
  },
  {
    term: "AI Share of Voice (ASOV)",
    short: "Your weighted presence inside AI answers, compared with every brand named alongside you.",
    long: "The share of AI-generated answers, across a fixed prompt set and model set, in which a brand appears, weighted by how prominently and how favorably it appears. Share of Model asks whether you showed up; ASOV asks how much of the answer was yours once you did. Measured with the six-step Monroya ASOV Framework.",
    alsoSee: [
      { label: "AI Share of Voice", to: "/ai-share-of-voice" },
      { label: "What is Share of Model", to: "/what-is-share-of-model" },
    ],
  },
  {
    term: "Buyer journey matrix",
    short: "Grid of buyer stages × competitors that maps where AI assistants cite you vs others.",
    long: "Monroya's home view. Rows are Discovery / Evaluation / Decision; columns are you and your competitor set. Each cell carries a score (0–100) and a list of the prompts driving it. Every cell with a gap is an actionable opportunity.",
    alsoSee: [{ label: "Buyer Journey Intelligence", to: "/buyer-journey-intelligence" }],
  },
  {
    term: "Citation",
    short: "An AI assistant linking to a specific URL as the source for a claim in its answer.",
    long: "Citations are extracted from the provider's native citation payload (Perplexity citations, Gemini grounding, ChatGPT browsing) plus a fallback URL parse. Citations are the strongest mention class, they drive click-through and rank-of-trust.",
  },
  {
    term: "Citation rate",
    short: "Share of runs in which your domain appears in an assistant's source ledger for a given prompt.",
    long: "Measured 0–100% per prompt per provider. Each prompt runs three times per scan; citation rate is the share of those runs that cite your domain. The headline AI visibility metric.",
    alsoSee: [{ label: "Methodology", to: "/methodology" }],
  },
  {
    term: "GEO",
    short: "Generative Engine Optimization.",
    long: "Optimizing for retrieval into a generated answer: getting your page pulled in and cited when a model answers a buyer question. Narrower than LLMO, which also covers what a model says about you from trained knowledge alone.",
    alsoSee: [{ label: "AEO", to: "/glossary#aeo" }, { label: "LLMO vs GEO", to: "/llmo-vs-geo" }],
  },
  {
    term: "Grounding",
    short: "The provider-side process of attaching real web sources to a generated answer.",
    long: "Google's term for the citation pipeline in Gemini. The structured groundingMetadata payload is the most reliable source extraction signal Gemini exposes.",
  },
  {
    term: "LLM optimization (LLMO)",
    short: "Improving how often, how accurately, and how favorably AI models describe your company.",
    long: "The practice of improving how often, how accurately, and how favorably your company appears in answers generated by ChatGPT, Gemini, Claude, and Perplexity. Measured with Share of Model. Broader than GEO: it covers both live retrieval and the model's trained knowledge of your brand.",
    alsoSee: [
      { label: "LLM optimization platform", to: "/large-language-model-optimization" },
      { label: "What is LLM optimization", to: "/large-language-model-optimization" },
      { label: "LLMO vs SEO", to: "/large-language-model-optimization" },
    ],
  },
  {
    term: "Mention class",
    short: "How an assistant references your brand: cited, named, category, or absent.",
    long: "Classifier output per prompt per provider. Cited = linked source. Named = mentioned by name without a link. Category = your category mentioned, you aren't. Absent = no mention at any level. Used alongside citation rate to score visibility.",
  },
  {
    term: "Opportunity",
    short: "A specific, ranked action that closes a measured visibility gap.",
    long: "Generated per buyer-journey matrix cell where your score trails the competitor set. Carries an estimated lift, an effort estimate, and a draft asset ready to ship.",
  },
  {
    term: "Prompt",
    short: "A query you want to track AI assistant behavior on.",
    long: "Mix of discovery, evaluation, and decision-stage questions your buyers ask. Generated during onboarding, edited by you, then run three times per provider per scan.",
  },
  {
    term: "Scan",
    short: "One full pass of every tracked prompt × every provider.",
    long: "Either weekly (automated, no quota impact) or manual (counts against your monthly limit, used to measure the lift from a specific change).",
  },
  {
    term: "Share of Model",
    short: "How frequently AI models recommend, mention, or position your brand across buyer questions.",
    long: "Share of Model = (answers naming your brand ÷ total answers sampled) × 100, held constant across a fixed prompt set, model set, and time window. The LLMO equivalent of share of voice, and most useful when split by buyer stage, model, and competitor.",
    alsoSee: [
      { label: "What is Share of Model", to: "/what-is-share-of-model" },
      { label: "How to improve it", to: "/what-is-share-of-model" },
    ],
  },
  {
    term: "Source ledger",
    short: "Per-prompt list of every URL the providers cited, normalized and deduped.",
    long: "The raw evidence behind every visibility score. Use it to find which competitor pages, Reddit threads, or third-party sources are doing the work.",
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is the typical AI citation rate for B2B brands in 2026?",
    a: "92% of tracked brands have a citation rate of 0%, meaning ChatGPT, Perplexity, Claude, and Gemini never cite them when buyers ask category-relevant questions. The remaining 8% capture nearly all AI-driven consideration in their categories.",
  },
  {
    q: "What's the difference between GEO, AEO, and LLMO?",
    a: "They all describe the same practice, optimizing content so generative AI assistants cite you as a source. GEO (Generative Engine Optimization), AEO (Answer Engine Optimization), and LLMO (Large Language Model Optimization) are interchangeable labels. Pick whichever your team uses.",
  },
  {
    q: "How is citation rate measured?",
    a: "Each tracked prompt runs three times per provider per scan. Citation rate is the share of those runs where your domain appears in the assistant's source ledger, expressed 0–100% per prompt per provider.",
  },
  {
    q: "What counts as a mention if there's no citation?",
    a: "Monroya classifies every response into six outcome classes: cited (linked source), named (mentioned by brand name without a link), category (your category mentioned, you aren't), and absent (no mention at any level).",
  },
  {
    q: "How often should AI visibility be scanned?",
    a: "Weekly automated scans establish trend lines. Manual scans are used to measure the lift from a specific change, such as publishing a comparison page or earning a Reddit citation, without waiting for the next weekly cycle.",
  },
];

export const Route = createFileRoute("/glossary")({
  head: () => {
    const base = marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Glossary", path: PATH },
      ],
    });
    const definedTermSet = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Monroya AI Search Glossary",
      url: `${SITE_URL}${PATH}`,
      hasDefinedTerm: TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.short,
        url: `${SITE_URL}${PATH}#${slug(t.term)}`,
      })),
    };
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        { type: "application/ld+json", children: JSON.stringify(definedTermSet) },
        { type: "application/ld+json", children: JSON.stringify(faqPage) },
      ],
    };
  },
  component: GlossaryPage,
});


function slug(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-"); }

function GlossaryPage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname.replace(/\/$/, "") !== PATH) return <Outlet />;
  return (
    <PublicShell>
      <article className="site-container px-5 py-16 sm:px-6">
        <header className="border-b border-border pb-8">
          <Stamp tone="slate">Glossary</Stamp>
          <h1 className="mt-5 font-serif text-4xl tracking-tight text-ink md:text-5xl">
            AI search terms, defined.
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-ink/80">
            <strong className="font-semibold text-ink">In 2026, 92% of tracked B2B brands have an AI citation rate of 0%</strong> - 
            ChatGPT, Perplexity, Claude, and Gemini never surface them when buyers ask
            category-relevant questions. The vocabulary for fixing that hasn't settled
            yet. Here's a plain-English reference for the terms that come up in
            evaluation and team discussions.
          </p>
        </header>

        {/* Term index */}
        <nav className="mt-8 flex flex-wrap gap-2">
          {TERMS.map((t) => (
            <a
              key={t.term}
              href={`#${slug(t.term)}`}
              className="rounded-md border border-border bg-paper px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:border-ink hover:text-ink"
            >
              {t.term}
            </a>
          ))}
        </nav>

        <dl className="mt-12 divide-y divide-border border-y border-border">
          {TERMS.map((t) => (
            <div key={t.term} id={slug(t.term)} className="scroll-mt-24 py-8">
              <dt className="font-serif text-2xl text-ink">{t.term}</dt>
              <dd className="mt-1 text-base font-medium text-ink/85">{t.short}</dd>
              <dd className="mt-3 leading-relaxed text-muted-foreground">{t.long}</dd>
              {t.alsoSee && (
                <dd className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Also see
                  </span>
                  {t.alsoSee.map((link) =>
                    link.to.startsWith("/glossary#") ? (
                      <a
                        key={link.to}
                        href={link.to.replace("/glossary", "")}
                        className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.to}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        to={link.to as unknown as never}
                        className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                </dd>
              )}
            </div>
          ))}
        </dl>

        <section id="faq" className="mt-16 border-t border-border pt-12">
          <Stamp tone="slate">FAQ</Stamp>
          <h2 className="mt-5 font-serif text-3xl tracking-tight text-ink">
            AI search, in five questions.
          </h2>
          <dl className="mt-8 divide-y divide-border border-y border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-serif text-xl text-ink">{f.q}</dt>
                <dd className="mt-3 leading-relaxed text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </article>
    </PublicShell>
  );
}
